import { fail, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { readData } from "@/office/excel/daten";
import { nichtPruefbar } from "../schnittstelle";

export const lektionDatenschnitte: Lesson = {
  id: "datenschnitte",
  title: "Datenschnitte und Berichtslayout",
  summary: "Filter, die man sieht – und eine Darstellung, mit der sich weiterarbeiten lässt.",
  durationMinutes: 11,
  learningGoals: [
    "Einen Datenschnitt anlegen und mit mehreren Auswertungen verbinden",
    "Das Berichtslayout einer PivotTable bewusst wählen",
    "Eine PivotTable als Datenquelle für weitere Auswertungen einschätzen",
  ],
  steps: [
    {
      id: "datenschnitt",
      title: "Ein Filter, den man sieht",
      explanation: [
        "Der Filterbereich einer PivotTable hat einen Nachteil: Ein aktiver Filter " +
          "ist nur an einem kleinen Trichtersymbol zu erkennen. Wer den Bericht " +
          "ausgedruckt vor sich hat, sieht nicht, dass nur eine Region enthalten ist – " +
          "und liest Zahlen, die er für das Ganze hält.",
        "Ein Datenschnitt löst das. Er ist eine Kachelleiste neben der PivotTable, in " +
          "der jede Kategorie als Schaltfläche steht. Die gewählten sind hervorgehoben, " +
          "die übrigen blass. Was gerade gefiltert ist, sieht man ohne Klicken – auch " +
          "auf Papier.",
        "Angelegt wird er über „PivotTable-Analyse“ → „Datenschnitt einfügen“, dann " +
          "die gewünschten Felder anhaken. Auch für gewöhnliche Tabellen gibt es " +
          "Datenschnitte, dort unter „Tabellenentwurf“.",
        "Der eigentliche Trumpf kommt bei mehreren Auswertungen: Über " +
          "„Verbindungen melden“ – rechte Maustaste auf den Datenschnitt – lässt sich " +
          "ein Datenschnitt mit mehreren PivotTables verbinden, sofern sie aus " +
          "derselben Datenbasis stammen. Ein Klick filtert dann das ganze Dashboard " +
          "gleichzeitig. Genau so entstehen die Übersichtsblätter, die aussehen, als " +
          "steckte ein Programm dahinter.",
        "Für Datumsfelder gibt es die Zeitachse als Sonderform: einen Schieberegler " +
          "über Jahre, Quartale, Monate oder Tage. Sie ist die angenehmere Wahl, " +
          "sobald mehr als eine Handvoll Zeiträume zur Auswahl stehen.",
      ],
      keyPoints: [
        "PivotTable-Analyse → Datenschnitt einfügen.",
        "Rechtsklick → Verbindungen melden verbindet ihn mit weiteren PivotTables.",
        "Für Datumsfelder ist die Zeitachse die passendere Form.",
      ],
      task: {
        instruction: "Füge deiner PivotTable einen Datenschnitt für eine Kategorie hinzu.",
        hint: "Cursor in die PivotTable, dann PivotTable-Analyse → Datenschnitt einfügen und ein Feld anhaken.",
        check: async () => {
          const daten = await readData();

          if (daten.slicers === null) {
            return nichtPruefbar(
              "Die Datenschnitte der Arbeitsmappe",
              "1.10",
              "Sieh nach, ob neben deiner PivotTable eine Kachelleiste mit den Kategorien steht.",
              true,
            );
          }

          if (daten.slicers.length === 0) {
            const pivots = daten.pivotTables ?? [];
            return fail(
              pivots.length === 0
                ? "Es gibt weder eine PivotTable noch einen Datenschnitt."
                : `Die ${pivots.length === 1 ? "PivotTable" : "PivotTables"} steht bereit, aber es ist ` +
                  "noch kein Datenschnitt angelegt.",
              "Setze den Cursor in die PivotTable und wähle PivotTable-Analyse → Datenschnitt einfügen.",
            );
          }

          const erster = daten.slicers[0];
          const auswahl =
            erster.selectedCount !== null && erster.itemCount !== null
              ? ` Gerade sind ${erster.selectedCount} von ${erster.itemCount} Einträgen ausgewählt.`
              : "";
          return pass(
            `Datenschnitt „${erster.caption ?? erster.name}“` +
              (erster.sheetName ? ` auf „${erster.sheetName}“` : "") +
              ` angelegt.${auswahl}`,
          );
        },
      },
    },
    {
      id: "berichtslayout",
      title: "Das Berichtslayout",
      explanation: [
        "Die Voreinstellung einer PivotTable heißt „Kurzformat“. Sie stapelt alle " +
          "Zeilenfelder in einer einzigen Spalte, eingerückt nach Ebene, und schreibt " +
          "als Überschrift „Zeilenbeschriftungen“. Das ist kompakt und für einen Blick " +
          "aufs Ergebnis völlig in Ordnung.",
        "Sobald jemand mit dem Ergebnis weiterarbeiten will, ist es die falsche Wahl. " +
          "Denn dann steht in der Spalte ein Mischmasch aus Regionen und Produkten, " +
          "und die Überschrift sagt nicht, was gemeint ist. Filtern, Sortieren, ein " +
          "SVERWEIS darauf – alles wird umständlich.",
        "Unter „Entwurf“ → „Berichtslayout“ gibt es zwei Alternativen. Die " +
          "Gliederungsform gibt jedem Feld eine eigene Spalte mit seinem richtigen " +
          "Namen als Überschrift. Die Tabellenform tut dasselbe und setzt die " +
          "Teilergebnisse ans Ende statt an den Anfang jeder Gruppe – das Ergebnis " +
          "sieht aus wie eine gewöhnliche Tabelle.",
        "Ergänzt um „Alle Elementnamen wiederholen“ – im selben Menü – entsteht daraus " +
          "eine vollständig gefüllte Liste ohne Lücken, die sich sofort " +
          "weiterverarbeiten lässt. Diese Kombination aus Tabellenform, wiederholten " +
          "Elementnamen und ausgeschalteten Teilergebnissen ist der Standardweg, um " +
          "aus einer PivotTable saubere Daten zu gewinnen.",
      ],
      keyPoints: [
        "Entwurf → Berichtslayout: Kurzformat, Gliederungsform oder Tabellenform.",
        "Tabellenform gibt jedem Feld eine eigene Spalte mit richtigem Namen.",
        "„Alle Elementnamen wiederholen“ füllt die Lücken für die Weiterverarbeitung.",
      ],
      task: {
        instruction:
          "Stelle deine PivotTable auf die Gliederungs- oder Tabellenform um, sodass " +
          "jedes Zeilenfeld eine eigene Spalte mit seinem Namen bekommt.",
        hint: "Cursor in die PivotTable, dann Entwurf → Berichtslayout → In Tabellenformat anzeigen.",
        check: async () => {
          const daten = await readData();

          if (daten.pivotTables === null) {
            return nichtPruefbar(
              "Die PivotTables der Arbeitsmappe",
              "1.3",
              "Sieh nach, ob deine PivotTable je Zeilenfeld eine eigene Spalte hat.",
              true,
            );
          }

          if (daten.pivotTables.length === 0) {
            return fail(
              "In der Arbeitsmappe gibt es noch keine PivotTable.",
              "Löse zuerst die Aufgabe aus der vorigen Lektion.",
            );
          }

          const mitLayout = daten.pivotTables.filter((pivot) => pivot.layoutType !== null);
          if (mitLayout.length === 0) {
            return nichtPruefbar(
              "Das Berichtslayout einer PivotTable",
              "1.8",
              "Sieh unter „Entwurf“ → „Berichtslayout“ nach, welcher Eintrag markiert ist.",
            );
          }

          const umgestellt = mitLayout.filter(
            (pivot) => pivot.layoutType === "Outline" || pivot.layoutType === "Tabular",
          );

          if (umgestellt.length === 0) {
            const erste = mitLayout[0];
            return fail(
              `Die PivotTable „${erste.name}“ steht auf dem Layout „${erste.layoutType}“ – dem ` +
                "Kurzformat. Alle Zeilenfelder stecken damit in einer Spalte mit der Überschrift " +
                "„Zeilenbeschriftungen“.",
              "Cursor in die PivotTable, dann Entwurf → Berichtslayout → „In Gliederungsformat anzeigen“ " +
                "oder „In Tabellenformat anzeigen“.",
            );
          }

          const erste = umgestellt[0];
          const benennung: Record<string, string> = { Outline: "Gliederungsform", Tabular: "Tabellenform" };
          return pass(
            `„${erste.name}“ steht auf der ${benennung[erste.layoutType ?? ""] ?? erste.layoutType} – ` +
              "jedes Zeilenfeld hat eine eigene Spalte mit seinem richtigen Namen als Überschrift.",
          );
        },
      },
    },
  ],
};
